/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';

const styles = cssMap({
	card: {
		boxSizing: 'border-box',
		width: '100%',
		height: '220px',
		minHeight: '220px',
		backgroundColor: token('elevation.surface.raised'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		overflow: 'hidden',
	},
	nextCard: {
		boxSizing: 'border-box',
		width: '100%',
		height: '150px',
		minHeight: '150px',
		backgroundColor: token('elevation.surface.raised'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		overflow: 'hidden',
	},
	breadcrumbs: {
		color: token('color.text.subtlest'),
	},
	nextSection: {
		paddingTop: token('space.200'),
	},
	rowContainer: {
		width: '100%',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.small'),
	},
	rowContent: {
		width: '100%',
		paddingRight: token('space.300'),
	},
	bannerContainer: {
		width: '100%',
		maxWidth: '1313px',
		marginLeft: 'auto',
		marginRight: 'auto',
	},
});

const spacesBreadcrumbMessage = defineMessage({
	id: 'servicedesk-solution-composer.breadcrumbs.spaces',
	defaultMessage: 'Spaces',
	description: 'Breadcrumb label for the Spaces link in the Getting Started skeleton loading state',
});

const gettingStartedBreadcrumbMessage = defineMessage({
	id: 'servicedesk-solution-composer.breadcrumbs.getting-started',
	defaultMessage: 'Getting started',
	description:
		'Breadcrumb label for the current Getting Started page in the skeleton loading state',
});

const SKELETON_CARD_COUNT = 6;

const gridStyles = cssMap({
	grid: {
		gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
		gridTemplateRows: '220px',
		gridAutoRows: '220px',
		width: '100%',
		maxWidth: '1313px',
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	nextGrid: {
		gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
		gridTemplateRows: '150px',
		gridAutoRows: '150px',
		width: '100%',
		maxWidth: '1313px',
		marginLeft: 'auto',
		marginRight: 'auto',
	},
});

/**
 * Renders 6 skeleton card placeholders for the Getting Started page.
 * Shared between the full-page skeleton and the in-grid loading fallback.
 */
export function GettingStartedSkeletonCards(): JSX.Element {
	return (
		<>
			{Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
				<Stack key={i} xcss={styles.card} space="space.200">
					<Skeleton width="100%" height="12px" borderRadius={token('radius.small')} />
					<SkeletonNavRows />
				</Stack>
			))}
		</>
	);
}

const containerStyles = cssMap({
	container: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		gap: token('space.200'),
	},
});

function NextSectionSkeletonCards(): JSX.Element {
	return (
		<>
			{Array.from({ length: 3 }, (_, i) => (
				<Stack key={i} xcss={styles.nextCard} space="space.200">
					<Skeleton width="100%" height="12px" borderRadius={token('radius.small')} />
					<SkeletonNavRows />
				</Stack>
			))}
		</>
	);
}

function SkeletonNavRows(): JSX.Element {
	return (
		<Stack space="space.100">
			{['70%', '55%', '60%'].map((width, i) => (
				<Box key={i} xcss={styles.rowContainer}>
					<Flex alignItems="center" gap="space.150" xcss={styles.rowContent}>
						<Skeleton width="24px" height="24px" borderRadius={token('radius.xsmall')} />
						<Skeleton width={width} height="14px" borderRadius={token('radius.small')} />
					</Flex>
				</Box>
			))}
		</Stack>
	);
}

/**
 * Banner skeleton + 3x2 card grid. Shared between the full-page skeleton
 * and the in-component Suspense fallback in solution-composer.
 *
 * Includes its own padding/gap container so callers get consistent spacing
 * regardless of context.
 */
export function GettingStartedSkeletonGrid(): JSX.Element {
	const { formatMessage } = useIntl();
	return (
		<Stack xcss={containerStyles.container} space="space.200">
			<Inline space="space.100" xcss={styles.breadcrumbs}>
				<Text size="medium" color="color.text.subtlest">
					{formatMessage(spacesBreadcrumbMessage)}
				</Text>
				<Text size="medium" color="color.text.subtlest">
					/
				</Text>
				<Text size="medium" color="color.text.subtlest">
					{formatMessage(gettingStartedBreadcrumbMessage)}
				</Text>
			</Inline>
			<Box xcss={styles.bannerContainer}>
				<Skeleton width="100%" height="100px" borderRadius={token('radius.medium')} />
			</Box>
			<Grid xcss={gridStyles.grid} gap="space.200" role="presentation">
				<GettingStartedSkeletonCards />
			</Grid>
			<Stack xcss={styles.nextSection} space="space.100">
				<Skeleton width="88px" height="12px" borderRadius={token('radius.small')} />
				<Skeleton width="286px" height="14px" borderRadius={token('radius.small')} />
			</Stack>
			<Grid xcss={gridStyles.nextGrid} gap="space.200" role="presentation">
				<NextSectionSkeletonCards />
			</Grid>
		</Stack>
	);
}
